import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./styles.css";
import "./cyberpunk-theme.css"; // token overrides under :root[data-theme="cyberpunk"] — must come after styles.css
import "./tmc-themes.css"; // more themes (deep/terra/aurora/arcade/yuletide/spooky/bttf) — after styles.css
import App from "./App.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";

// Register the service worker (push notifications). Best-effort.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Auto-update: when a new build is deployed, installed/open clients pick it up without a manual
// reinstall. The running bundle carries __BUILD_ID__; /build-id.txt reflects the LATEST deployed
// build. If they differ we reload (which — with index.html served no-cache — pulls the new bundle).
// Checked when the tab regains focus/visibility and periodically, so an idle open app self-heals
// rather than sitting on stale code. Reloading on focus avoids yanking the page mid-interaction.
const BUILD_ID = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "";
// A new deploy no longer yanks the page the moment you come back to it. Instead:
//   • the app has been in the background 10+ minutes (you weren't using it) → reload quietly;
//   • otherwise → show the "new version" banner (UpdateBanner) and let the person pick the moment.
// Never while a huddle is live, and never with an unsent message in a composer.
const AWAY_RELOAD_MS = 10 * 60 * 1000;
let reloading = false;
let hiddenSince = document.visibilityState === "hidden" ? Date.now() : 0;
const hasDraft = () => [...document.querySelectorAll(".composer__input")].some((el) => (el.value || "").trim());
const safeToReload = () => !window.__tmcMeetingActive && !hasDraft();
async function checkForUpdate(awayMs = 0) {
  if (reloading || !BUILD_ID) return;
  try {
    const res = await fetch("/build-id.txt", { cache: "no-store" });
    if (!res.ok) return;
    const latest = (await res.text()).trim();
    if (!latest || latest === BUILD_ID) return;
    const away = Math.max(awayMs, hiddenSince ? Date.now() - hiddenSince : 0);
    if (away >= AWAY_RELOAD_MS && safeToReload()) {
      reloading = true;
      location.reload();
      return;
    }
    window.__tmcUpdateReady = true;
    window.dispatchEvent(new Event("tmc:update-ready"));
  } catch { /* offline / dev — ignore */ }
}
window.addEventListener("focus", () => checkForUpdate());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") { hiddenSince = Date.now(); return; }
  const away = hiddenSince ? Date.now() - hiddenSince : 0;
  hiddenSince = 0;
  checkForUpdate(away);
});
setInterval(() => checkForUpdate(), 5 * 60 * 1000);

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <BrowserRouter>
      <ErrorBoundary title="TMC Chat hit an error">
        <App />
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
);
